import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import 'regenerator-runtime/runtime'
import MobileAppPopup from '../../components/MobileAppPopup'
import { getPrerenderedItemsByIdeaId, getPrerenderedItemsByIds, getPrerenderedPost, getPrerenderedUserDesigns } from '../../queries/users'
import { setPrerenderedDetails } from '../../redux/actions/DesignDetailsAction'
import DesignDetails from '../../routes/DesignDetails'

const Routes = ({ post, userDesigns, preloadedItems }) => {
  const dispatch = useDispatch()
  const { query } = useRouter()

  const designSlug = post?.urlSlug || query.designId || post?.objectId
  const canonicalUrl = designSlug ? `https://decormatters.com/design/${designSlug}` : 'https://decormatters.com/design'
  const designImage =
    post?.image ||
    post?.designThumbnail ||
    post?.cfImageUrl ||
    post?.cfThumbImageUrl ||
    post?.thumbImageFile?.url ||
    post?.feedImageFile?.url ||
    post?.cfBgImageUrl ||
    undefined
  const interactionStatistic = []

  if (typeof post?.numLikes === 'number') {
    interactionStatistic.push({
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'LikeAction' },
      userInteractionCount: post.numLikes
    })
  }

  if (typeof post?.numComments === 'number') {
    interactionStatistic.push({
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'CommentAction' },
      userInteractionCount: post.numComments
    })
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': canonicalUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    url: canonicalUrl,
    name: post?.metaTitle || post?.aiTitle || post?.title || 'DecorMatters design',
    headline: post?.metaTitle || post?.aiTitle || post?.title,
    description: post?.metaDescription || post?.aiDescription || post?.title,
    image: designImage,
    datePublished: post?.createdAt,
    dateModified: post?.updatedAt || post?.createdAt,
    isAccessibleForFree: true,
    author: post?.user
      ? {
          '@type': 'Person',
          name: post.user.uniqueDisplayName || post.user.username || 'DecorMatters creator'
        }
      : undefined,
    publisher: {
      '@type': 'Organization',
      name: 'DecorMatters',
      url: 'https://decormatters.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://decormatters.com/logo192.png'
      }
    },
    interactionStatistic
  }

  const imageStructuredData = designImage
    ? {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        '@id': `${canonicalUrl}#image`,
        contentUrl: designImage,
        url: designImage,
        name: post?.metaTitle || post?.aiTitle || post?.title || 'DecorMatters design',
        description: post?.metaDescription || post?.aiDescription || post?.title,
        creator: post?.user
          ? {
              '@type': 'Person',
              name: post.user.uniqueDisplayName || post.user.username || 'DecorMatters creator'
            }
          : undefined,
        creditText: post?.user?.uniqueDisplayName || post?.user?.username || 'DecorMatters creator',
        copyrightNotice: 'DecorMatters',
        license: 'https://decormatters.com/terms'
      }
    : null

  useEffect(() => {
    if (post && post.objectId) {
      const lastPartId = query.designId?.split('-').pop()
      dispatch(setPrerenderedDetails(lastPartId || post.objectId, post))
    }
  }, [dispatch, post, query.designId])

  return (
    <>
      <Head>
        <title>{post?.metaTitle || `@${post?.user?.uniqueDisplayName || 'user'} on DecorMatters: "${post.title}"`}</title>
        <meta name="description" content={post?.metaDescription || `${post?.numLikes} Likes, ${post?.numComments} Comments – @${post?.user?.uniqueDisplayName || 'user'} on DecorMatters: "${post.title}"`} />
        <meta name="twitter:site" content="www.decormatters.com" />
        <meta name="twitter:title" content={post?.metaTitle || `@${post?.user?.uniqueDisplayName || 'user'} on DecorMatters: "${post.title}"`} />

        <meta property="og:title" content={post?.metaTitle || `@${post?.user?.uniqueDisplayName || 'user'} on DecorMatters: "${post.title}"`} />
        <meta property="og:description" content={post?.metaDescription || `${post?.numLikes} Likes, ${post?.numComments} Comments – @${post?.user?.uniqueDisplayName || 'user'} on DecorMatters: "${post.title}"`} />
        {designSlug && <link rel="canonical" href={canonicalUrl} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData)
          }}
        />
        {imageStructuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(imageStructuredData)
            }}
          />
        )}
      </Head>
      <DesignDetails initialPost={post} userDesigns={userDesigns} preloadedItems={preloadedItems} />
      <MobileAppPopup />
    </>
  )
}

export default Routes

export async function getServerSideProps({ query, req, res }) {
  try {
    const lastPartId = query.designId.split('-').pop()
    const post = await getPrerenderedPost(lastPartId)

    let userDesigns = []
    let preloadedItems = null

    if (post?.result?.userInspiration?.user?.objectId) {
      try {
        const userDesignsResponse = await getPrerenderedUserDesigns(post.result.userInspiration.user.objectId, 8)
        // console.log('userDesignsResponse', userDesignsResponse)
        userDesigns = userDesignsResponse?.result?.userInspirations || []
        // Filter out the current design to avoid showing it again
        userDesigns = userDesigns.filter(design => design.objectId !== lastPartId)
      } catch (userDesignsError) {
        console.error('Failed to fetch user designs:', userDesignsError)
      }
    }

    // Preload items for the design
    if (post?.result?.userInspiration) {
      try {
        const inspiration = post.result.userInspiration
        if (inspiration.itemIds && inspiration.itemIds.length > 0) {
          // Use itemIds if available
          const itemsResponse = await getPrerenderedItemsByIds(inspiration.itemIds)

          preloadedItems = {
            type: 'itemIds',
            items: itemsResponse?.result?.items || []
          }
        } else if (inspiration.idea?.objectId) {
          // Fallback to idea objectId
          const ideaResponse = await getPrerenderedItemsByIdeaId(inspiration.idea.objectId)
          const items = ideaResponse?.idea?.productsWithPosition || []
          preloadedItems = {
            type: 'ideaId',
            items: items.map(item => item.item || item.usrItem || item)
          }
        }
      } catch (itemsError) {
        console.error('Failed to fetch items:', itemsError)
        preloadedItems = null
      }
    }

    res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=59')

    return {
      props: {
        post: post?.result?.userInspiration || {},
        userDesigns,
        preloadedItems
      }
    }
  } catch (error) {
    return {
      props: {
        post: {},
        userDesigns: [],
        preloadedItems: null
      }
    }
  }
}
